import { useMemo } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import type { Event } from "@shared/schema";

/* ─── SHARED POSTER-WALL DATA ─────────────────────────────────────────────
   One hook that fetches the workshop poster data ONCE and returns the sorted
   poster models + the "next up" slug. Both the baseline WorkshopPosterWall and
   the three Poster-Studio riffs read from this. React Query dedupes by
   queryKey, so even multiple callers share the same network requests — but the
   Poster-Studio page lifts this to the top and passes `posters`/`nextUpSlug`
   down as props so the four sections don't each spin up their own hook trees.

   Live data:
     /api/events                 → soonest upcoming published session per series
     /api/events/:seriesSlug     → lowest ticketTypes.priceCents ("from")
     /api/workshop-series        → fullName
*/

export type PosterTone = {
  /** soft printed color field */
  field: string;
  /** hover-deepened field */
  fieldDeep: string;
  /** hairline + index accent for this poster */
  rule: string;
};

type PosterDef = {
  /** riso icon key (file at /images/workshop/icons/<key>.png|webp) */
  iconKey: string;
  tone: PosterTone;
};

/* Series slug → icon key + this poster's soft color field (tokens in index.css). */
export const SERIES_POSTERS: Record<string, PosterDef> = {
  "claude-code-for-beginners-workshop": {
    iconKey: "claude-code",
    tone: { field: "var(--poster-code)", fieldDeep: "var(--poster-code-deep)", rule: "var(--poster-code-rule)" },
  },
  "advanced-claude-code-workshop": {
    iconKey: "claude-code-advanced",
    tone: { field: "var(--poster-advanced)", fieldDeep: "var(--poster-advanced-deep)", rule: "var(--poster-advanced-rule)" },
  },
  "claude-cowork-for-beginners-workshop": {
    iconKey: "claude-cowork",
    tone: { field: "var(--poster-cowork)", fieldDeep: "var(--poster-cowork-deep)", rule: "var(--poster-cowork-rule)" },
  },
  "claude-for-design-workshop": {
    iconKey: "claude-design",
    tone: { field: "var(--poster-design)", fieldDeep: "var(--poster-design-deep)", rule: "var(--poster-design-rule)" },
  },
  "claude-for-sales-teams-workshop": {
    iconKey: "claude-sales",
    tone: { field: "var(--poster-sales)", fieldDeep: "var(--poster-sales-deep)", rule: "var(--poster-sales-rule)" },
  },
  "the-ai-first-leader-workshop": {
    iconKey: "ai-fundamentals",
    tone: { field: "var(--poster-leader)", fieldDeep: "var(--poster-leader-deep)", rule: "var(--poster-leader-rule)" },
  },
};

/* Canonical wall order, independent of DB display order — used as the
   fallback name + when a series has no upcoming session to sort by. */
export const POSTER_ORDER: string[] = [
  "claude-for-design-workshop",
  "claude-code-for-beginners-workshop",
  "the-ai-first-leader-workshop",
  "advanced-claude-code-workshop",
  "claude-cowork-for-beginners-workshop",
  "claude-for-sales-teams-workshop",
];

const FALLBACK_NAME: Record<string, string> = {
  "claude-for-design-workshop": "The Claude for Design Workshop",
  "claude-code-for-beginners-workshop": "The Claude Code for Beginners Workshop",
  "the-ai-first-leader-workshop": "The AI-First Leader Workshop",
  "advanced-claude-code-workshop": "The Advanced Claude Code Workshop",
  "claude-cowork-for-beginners-workshop": "The Claude Cowork for Beginners Workshop",
  "claude-for-sales-teams-workshop": "The Claude for Sales Teams Workshop",
};

export const ICON_BASE = "/images/workshop/icons";

export const FAR_FUTURE = Number.MAX_SAFE_INTEGER;

/* Strip the leading "The " and trailing " Workshop" so the serif title reads
   like a poster headline ("Claude for Design"), not a sentence. */
export function posterTitle(fullName: string): string {
  return fullName
    .replace(/^The\s+/i, "")
    .replace(/\s+Workshop$/i, "")
    .trim();
}

function isBookable(e: Event): boolean {
  if (e.status === "completed" || e.status === "cancelled") return false;
  if (!e.published) return false;
  return new Date(e.endDatetime as unknown as string) > new Date();
}

function fmtDate(startStr: string, timezone: string): string {
  const d = new Date(startStr);
  return d
    .toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: timezone })
    .toUpperCase();
}

/** Split a soonest-session start into the brutalist date-cell parts. */
function dateCellParts(startStr: string, timezone: string) {
  const d = new Date(startStr);
  return {
    month: d.toLocaleDateString("en-US", { month: "short", timeZone: timezone }).toUpperCase(),
    day: d.toLocaleDateString("en-US", { day: "numeric", timeZone: timezone }),
    weekday: d.toLocaleDateString("en-US", { weekday: "short", timeZone: timezone }).toUpperCase(),
  };
}

/** Start time in the workshop's own timezone, e.g. "10:00 AM ET". The "ET" is
   hardcoded — every workshop runs America/New_York (schema default), so we want
   the clean label rather than a long IANA / GMT-offset abbreviation. */
function startTimeLabel(startStr: string, timezone: string): string {
  const t = new Date(startStr)
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: timezone,
    })
    // "10:00 AM" → keep as-is; collapse non-breaking narrow space some envs add
    .replace(/ /g, " ");
  return `${t} ET`;
}

/** Start–end time range in the workshop's own timezone, e.g.
   "10:00 AM – 1:15 PM ET". Uses an en-dash; the "ET" is hardcoded (every
   workshop runs America/New_York — schema default). Returns "" if invalid. */
function timeRangeLabel(startStr: string, endStr: string, timezone: string): string {
  const start = new Date(startStr);
  const end = new Date(endStr);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return "";
  const fmt = (d: Date) =>
    d
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: timezone,
      })
      .replace(/ /g, " ");
  return `${fmt(start)} – ${fmt(end)} ET`;
}

export type PosterModel = {
  seriesSlug: string;
  iconKey: string;
  tone: PosterTone;
  title: string;
  /** soonest upcoming session, if any */
  dateLabel: string | null;
  /** "JUN" / "17" / "WED" — split parts of the soonest date, null when none */
  month: string | null;
  day: string | null;
  weekday: string | null;
  /** soonest session start time in ET, e.g. "10:00 AM ET" — null when none */
  startTime: string | null;
  /** soonest session start–end range in ET, e.g. "10:00 AM – 1:15 PM ET" —
     null when none */
  timeRange: string | null;
  sortTime: number; // ms; far-future when no upcoming session
  /** "from $599" — null until the detail query resolves */
  fromLabel: string | null;
  /** lowest whole-dollar price, null until the detail query resolves */
  fromPrice: number | null;
};

export type UpcomingWorkshops = {
  posters: PosterModel[];
  /** the soonest dated poster's slug — gets the NEXT UP flag */
  nextUpSlug: string | null;
};

/** @deprecated retained alias — use UpcomingWorkshops. */
export type PosterWallData = UpcomingWorkshops;

/**
 * Fetch + assemble the sorted poster models. Soonest dated workshop first;
 * series with no upcoming session sink to the end (keeping canonical order).
 */
export function useUpcomingWorkshops(): UpcomingWorkshops {
  const eventsQ = useQuery<Event[]>({ queryKey: ["/api/events"] });
  const seriesQ = useQuery<{ slug: string; fullName: string }[]>({
    queryKey: ["/api/workshop-series"],
  });

  /* Soonest bookable session per series slug. */
  const soonestBySeries = useMemo(() => {
    const map = new Map<string, Event>();
    for (const e of eventsQ.data ?? []) {
      const slug = e.seriesSlug;
      if (!slug || !SERIES_POSTERS[slug] || !isBookable(e)) continue;
      const t = new Date(e.startDatetime as unknown as string).getTime();
      const cur = map.get(slug);
      if (!cur || t < new Date(cur.startDatetime as unknown as string).getTime()) {
        map.set(slug, e);
      }
    }
    return map;
  }, [eventsQ.data]);

  /* One detail fetch per series → lowest ticket price ("from"). The list
     endpoint omits prices, so each series detail is needed. */
  const priceQueries = useQueries({
    queries: POSTER_ORDER.map((slug) => ({
      queryKey: [`/api/events/${slug}`],
      staleTime: 5 * 60 * 1000,
    })),
  });

  const fromBySeries = useMemo(() => {
    const map = new Map<string, number>();
    POSTER_ORDER.forEach((slug, i) => {
      const data = priceQueries[i]?.data as
        | { ticketTypes?: { priceCents: number }[] }
        | undefined;
      const prices = (data?.ticketTypes ?? [])
        .map((t) => t.priceCents)
        .filter((c): c is number => typeof c === "number" && c > 0);
      if (prices.length) map.set(slug, Math.min(...prices));
    });
    return map;
    // priceQueries is a new array each render; key on the resolved data only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceQueries.map((q) => (q.data ? "1" : "0")).join("")]);

  const nameBySeries = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of seriesQ.data ?? []) map.set(s.slug, s.fullName);
    return map;
  }, [seriesQ.data]);

  const posters: PosterModel[] = useMemo(() => {
    const list = POSTER_ORDER.map((slug) => {
      const def = SERIES_POSTERS[slug];
      const soonest = soonestBySeries.get(slug);
      const fullName = nameBySeries.get(slug) ?? FALLBACK_NAME[slug] ?? slug;
      const fromCents = fromBySeries.get(slug);
      const parts = soonest
        ? dateCellParts(soonest.startDatetime as unknown as string, soonest.timezone)
        : null;
      return {
        seriesSlug: slug,
        iconKey: def.iconKey,
        tone: def.tone,
        title: posterTitle(fullName),
        dateLabel: soonest
          ? fmtDate(soonest.startDatetime as unknown as string, soonest.timezone)
          : null,
        month: parts?.month ?? null,
        day: parts?.day ?? null,
        weekday: parts?.weekday ?? null,
        startTime: soonest
          ? startTimeLabel(soonest.startDatetime as unknown as string, soonest.timezone)
          : null,
        timeRange: soonest
          ? timeRangeLabel(
              soonest.startDatetime as unknown as string,
              soonest.endDatetime as unknown as string,
              soonest.timezone,
            )
          : null,
        sortTime: soonest
          ? new Date(soonest.startDatetime as unknown as string).getTime()
          : FAR_FUTURE,
        fromLabel:
          typeof fromCents === "number"
            ? `from $${Math.round(fromCents / 100).toLocaleString("en-US")}`
            : null,
        fromPrice: typeof fromCents === "number" ? Math.round(fromCents / 100) : null,
      } satisfies PosterModel;
    });
    // Soonest date first; series with no upcoming session sink to the end but
    // keep their canonical order relative to each other.
    return list.sort((a, b) => a.sortTime - b.sortTime);
  }, [soonestBySeries, fromBySeries, nameBySeries]);

  const nextUpSlug = posters.find((p) => p.sortTime !== FAR_FUTURE)?.seriesSlug ?? null;

  return { posters, nextUpSlug };
}

/** @deprecated retained alias — use useUpcomingWorkshops. */
export const usePosterWallData = useUpcomingWorkshops;
